import { useQuery } from "@tanstack/react-query";
import React from "react";
import useApi from "../../../shared/hooks/useApi";
import { PatientId } from "../../../shared/schema/schema";
import { usePatients } from "../../patient/hooks/usePatients";

export type PatientSearchSelection = { id: PatientId; label: string };

const MIN_SEARCH_LENGTH = 2;
const MAX_RESULTS = 20;

/**
 * The state behind a "Patient" filter that searches instead of listing.
 *
 * Searched in the browser, over the agency's patient list (loaded once and cached) narrowed to the
 * daily-note offices the caller may see -- the same offices the "Office" filter offers -- so the
 * filter never offers a patient whose notes the caller is not shown.
 *
 * An agency has far more patients than a dropdown can hold, so `Select` owns the search box and
 * this feeds it at most `MAX_RESULTS` matches. The chosen patient is remembered by name, because
 * `Select` renders the label of whichever option matches the current value and falls back to
 * printing the raw value when none does -- and the results empty out as soon as the search box is
 * cleared, so without this the filter would show the patient's id the moment the dropdown closed.
 *
 * `initial` restores a patient chosen before, so a filter brought back from an earlier visit to
 * the page shows the name rather than the id.
 */
export function usePatientSearchFilter(initial: PatientSearchSelection | null = null) {
  const { queries } = useApi();
  const patients = usePatients();
  const offices = useQuery(queries.dailyNotes.reviewOffices());

  const [search, setSearch] = React.useState("");
  const [selected, setSelected] = React.useState<PatientSearchSelection | null>(initial);

  const options = React.useMemo(() => {
    const term = search.trim().toLowerCase();
    const officeIds = new Set((offices.data ?? []).map((office) => office.id));

    const found =
      term.length < MIN_SEARCH_LENGTH
        ? []
        : (patients.data ?? [])
            .filter((patient) => officeIds.has(patient.currentOfficeId))
            .filter((patient) =>
              [
                `${patient.firstName} ${patient.lastName}`,
                `${patient.lastName} ${patient.firstName}`,
                String(patient.id),
                patient.displayId === null ? "" : String(patient.displayId),
              ].some((text) => text.toLowerCase().includes(term))
            )
            .slice(0, MAX_RESULTS)
            .map((patient) => ({
              id: patient.id,
              label: `${patient.firstName} ${patient.lastName}`,
            }));

    if (selected === null || found.some((p) => p.id === selected.id)) {
      return found;
    }

    return [selected, ...found];
  }, [search, patients.data, offices.data, selected]);

  return {
    search,
    setSearch,
    isFetching: patients.isFetching || offices.isFetching,
    options,
    selected,
    /** Call with the value the `Select` reports, so the label survives the results clearing. */
    remember: (id: PatientId | null | undefined) =>
      setSelected(options.find((option) => option.id === id) ?? null),
  };
}
